import './RequestGeneticCounseling.scss';

import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Notification, type NotificationAction } from '@myome/ui';

import { MYOME_SUPPORT_EMAIL, ORDER_STATUS_STEPS } from '@constants/constants';
import { OrderStatus } from '@customTypes/order';
import { GeneticCounselingRequest, Requisition } from '@customTypes/requisition';
import { getRequisitionStatusKey } from '@helpers/requisition';
import { Context } from '@store/store';
import { formatDate, recordGoogleAnalyticsEvent } from '@utils/helpers';

interface IProps {
  requisitionId?: string;
  /**
   * Individual identifier of a minor dependent when a guardian is viewing that
   * minor's results. Omitted for the logged-in user's own counseling.
   */
  viewing?: string;
  /**
   * The `viewing` individual's requisition and counseling record. Only read when
   * `viewing` is set; otherwise both come from the logged-in user's store.
   */
  viewedRequisition?: Requisition | null;
  viewedCounseling?: GeneticCounselingRequest | null;
  viewedFirstName?: string;
}

const RequestGeneticCounseling = ({
  requisitionId,
  viewing,
  viewedRequisition,
  viewedCounseling,
  viewedFirstName,
}: IProps) => {
  const [state] = useContext(Context);
  const { pathname } = useLocation();

  const [gcTitle, setGcTitle] = useState('Genetic counseling is available');
  const [gCStatusText, setGcStatusText] = useState<ReactNode>('');
  const [gcBtnTitle, setGcBtnTitle] = useState('');
  const [gcBtnLink, setGcBtnLink] = useState('');
  const [gcRedirectUrl, setGcRedirectUrl] = useState('');

  const testRequest = useMemo(
    () =>
      viewing
        ? (viewedRequisition ?? undefined)
        : state.testRequests.find((req: Requisition) => req.requisition_id === requisitionId),
    [viewing, viewedRequisition, state.testRequests, requisitionId],
  );
  const counselingDetails: GeneticCounselingRequest | null = viewing
    ? (viewedCounseling ?? null)
    : state.geneticCounselingDetails;
  const canRequestGC = useMemo(
    () => requisitionId && ORDER_STATUS_STEPS.find(o => o.step === testRequest?.orders?.[0]?.status),
    [testRequest, requisitionId],
  );
  // The counseling page needs to know whose access code to show, and `viewing` has to
  // survive a refresh there, so it travels in the URL rather than in router state.
  const geneticCounselingUrl = useMemo(
    () =>
      viewing
        ? `/${requisitionId}/genetic_counseling?viewing=${encodeURIComponent(viewing)}`
        : `/${requisitionId}/genetic_counseling`,
    [requisitionId, viewing],
  );
  // When a guardian is viewing a minor, the reader is the guardian and the subject is
  // the minor, so the copy talks about the dependent by name instead of "you".
  const subject = viewing ? viewedFirstName || 'your family member' : null;

  useEffect(() => {
    setGcTitle(subject ? `Genetic counseling is available for ${subject}` : 'Genetic counseling is available');
    // Every branch below sets only the fields it needs, so clear the rest first;
    // otherwise switching profiles can leave one person's appointment button on
    // another person's card.
    setGcStatusText('');
    setGcBtnTitle('');
    setGcBtnLink('');
    setGcRedirectUrl('');
    if (counselingDetails) {
      const { appointment_url, appointment_date, external_status, service_type } = counselingDetails;
      // if they have NOT scheduled an appointment:
      if (service_type === 'POST_TEST_REVIEW_ONLY') {
        // this means there review is complete and they are ready to book
        const accumulatedOrdersStatus = getRequisitionStatusKey(testRequest);
        if (accumulatedOrdersStatus === OrderStatus.COMPLETED) {
          setGcStatusText(
            subject
              ? `${subject}'s results are available below. If you have any questions after viewing them, you may schedule a session with a genetic counselor`
              : 'Your results are available below. If you have any questions after viewing your results, you may schedule a session with a genetic counselor',
          );
          setGcBtnTitle('Request Genetic Counseling');
          setGcBtnLink(geneticCounselingUrl);
        } else {
          setGcStatusText('');
        }
        // if they have already scheduled an appointment:
      } else if (service_type === 'POST_TEST_COUNSELING') {
        switch (external_status) {
          case 'COMPLETED':
            setGcStatusText(
              <>
                {subject
                  ? `${subject}'s genetic counseling session is complete. If you have any further questions, please contact our support team at `
                  : 'You have completed your genetic counseling session. If you have any further questions, please contact our support team at '}
                <a href={`mailto:${MYOME_SUPPORT_EMAIL}`}>{MYOME_SUPPORT_EMAIL}</a>
              </>,
            );
            break;
          case 'IN_PROGRESS':
            if (appointment_date) {
              setGcStatusText(
                subject
                  ? `${subject} has an upcoming genetic counseling session on ` +
                      `${formatDate(appointment_date)}. You can modify or cancel the ` +
                      'appointment by clicking on the button to your right'
                  : 'You have an upcoming genetic counseling session on ' +
                      `${formatDate(appointment_date)}. You can modify or cancel your ` +
                      'appointment by clicking on the button to your right',
              );
              setGcBtnTitle('View Details');
              setGcRedirectUrl(appointment_url ?? '');
            } else {
              setGcTitle('Genetic counseling initiated');
              setGcStatusText(
                subject
                  ? `We have initiated a request for a complimentary genetic counseling session for ${subject} with our partner, DNAvisit. You should have received an email from them directly to schedule the appointment. Please contact us if you did not receive the email or if you have any questions.`
                  : 'We have initiated a request for a complimentary genetic counseling session with our partner, DNAvisit. You should have received an email from them directly to schedule your appointment. Please contact us if you did not receive the email or if you have any questions.',
              );
            }
            break;
          case 'CANCELED':
            setGcStatusText(
              subject
                ? `${subject}'s genetic counseling appointment has been canceled. If this was not intentional, please reschedule the appointment`
                : 'Your genetic counseling session appointment has been canceled. If this was not intentional, please reschedule your appointment',
            );
            setGcBtnTitle('Reschedule');
            setGcBtnLink(geneticCounselingUrl);
            break;
          case 'INCOMPLETE':
            setGcStatusText(
              subject
                ? `It looks like ${subject} missed their appointment with DNAvisit. Please reschedule the session if you would like to discuss the results with a genetic counselor`
                : 'It looks like you missed your appointment with DNAvisit. Please reschedule your session if you would like to discuss your results with a genetic counselor',
            );
            setGcBtnTitle('Reschedule');
            setGcBtnLink(geneticCounselingUrl);
            break;
        }
      }
    } else {
      setGcStatusText('');
    }
  }, [counselingDetails, testRequest, geneticCounselingUrl, subject]);

  const handleButtonClick = () => {
    recordGoogleAnalyticsEvent('User Interaction', 'Button Click', `${gcBtnTitle} Button`);
  };

  if (!canRequestGC || !gCStatusText) {
    return null;
  }

  let action: NotificationAction | undefined;
  if (gcBtnTitle && gcRedirectUrl) {
    action = { label: gcBtnTitle, href: gcRedirectUrl, onClick: handleButtonClick };
  } else if (gcBtnTitle && gcBtnLink) {
    action = { label: gcBtnTitle, to: gcBtnLink, state: { pathname }, onClick: handleButtonClick };
  }

  return (
    <Notification variant='info' title={gcTitle} titleAs='h6' action={action} className='genetic-counseling'>
      <p>{gCStatusText}</p>
    </Notification>
  );
};

export default RequestGeneticCounseling;
